/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

// src/views/Dashboard/Faturalar.js

import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Text,
  Button,
  HStack,
  Icon,
  Badge,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Grid,
  GridItem,
  useToast
} from "@chakra-ui/react";
import { DataTable } from "components/DataTable";
import { FiPlus, FiDollarSign, FiTrendingDown, FiFileText, FiCreditCard } from "react-icons/fi";

export default function Faturalar() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  
  // Fatura Verileri
  const [bizeKesilen] = useState([
    { 
      id: 1, 
      faturaNo: "FTR-001", 
      tarih: "2025-09-01", 
      firma: "ABC Teknoloji", 
      tutar: 2500, 
      kdv: 450, 
      toplam: 2950,
      durum: "Ödendi",
      vadeTarihi: "2025-09-15"
    },
    { 
      id: 2, 
      faturaNo: "FTR-002", 
      tarih: "2025-09-10", 
      firma: "XYZ Ltd.", 
      tutar: 3700, 
      kdv: 666, 
      toplam: 4366,
      durum: "Beklemede",
      vadeTarihi: "2025-09-25"
    }
  ]);

  const [kestigimiz] = useState([
    { 
      id: 1, 
      faturaNo: "IXP-001", 
      tarih: "2025-09-05", 
      musteri: "Ahmet Lojistik", 
      tutar: 4200, 
      kdv: 756, 
      toplam: 4956,
      durum: "Tahsil Edildi",
      vadeTarihi: "2025-09-20"
    },
    { 
      id: 2, 
      faturaNo: "IXP-002", 
      tarih: "2025-09-15", 
      musteri: "Veli Nakliyat", 
      tutar: 1800, 
      kdv: 324, 
      toplam: 2124,
      durum: "Beklemede",
      vadeTarihi: "2025-09-30"
    }
  ]);

  const [hesapHareketleri] = useState([
    { 
      id: 1, 
      tarih: "2025-09-02", 
      aciklama: "Müşteri Tahsilat - IXP-001", 
      giris: 4956, 
      cikis: 0,
      bakiye: 15300,
      tip: "tahsilat"
    },
    { 
      id: 2, 
      tarih: "2025-09-12", 
      aciklama: "Ofis Kira Gideri", 
      giris: 0, 
      cikis: 750,
      bakiye: 14550,
      tip: "gider"
    }
  ]);

  // Tablolar için kolonlar (DataTable accessor kullanıyor, key değil)
  const bizeKesilenColumns = [
    { accessor: "faturaNo", Header: "Fatura No", sortable: true },
    { accessor: "tarih", Header: "Tarih", sortable: true },
    { accessor: "firma", Header: "Firma" },
    { 
      accessor: "tutar", 
      Header: "Tutar", 
      Cell: ({ value }) => `${value.toLocaleString()} ₺` 
    },
    { 
      accessor: "kdv", 
      Header: "KDV", 
      Cell: ({ value }) => `${value.toLocaleString()} ₺` 
    },
    { 
      accessor: "toplam", 
      Header: "Toplam", 
      Cell: ({ value }) => `${value.toLocaleString()} ₺` 
    },
    { 
      accessor: "durum", 
      Header: "Durum", 
      Cell: ({ value }) => (
        <Badge 
          colorScheme={value === "Ödendi" ? "green" : value === "Beklemede" ? "yellow" : "red"}
          variant="subtle"
        >
          {value}
        </Badge>
      )
    },
    { accessor: "vadeTarihi", Header: "Vade Tarihi" }
  ];

  const kestigimizColumns = [
    { accessor: "faturaNo", Header: "Fatura No", sortable: true },
    { accessor: "tarih", Header: "Tarih", sortable: true },
    { accessor: "musteri", Header: "Müşteri" },
    { 
      accessor: "tutar", 
      Header: "Tutar", 
      Cell: ({ value }) => `${value.toLocaleString()} ₺` 
    },
    { 
      accessor: "kdv", 
      Header: "KDV", 
      Cell: ({ value }) => `${value.toLocaleString()} ₺` 
    },
    { 
      accessor: "toplam", 
      Header: "Toplam", 
      Cell: ({ value }) => `${value.toLocaleString()} ₺` 
    },
    { 
      accessor: "durum", 
      Header: "Durum", 
      Cell: ({ value }) => (
        <Badge 
          colorScheme={value === "Tahsil Edildi" ? "green" : value === "Beklemede" ? "yellow" : "red"}
          variant="subtle"
        >
          {value}
        </Badge>
      )
    },
    { accessor: "vadeTarihi", Header: "Vade Tarihi" }
  ];

  const hesapHareketleriColumns = [
    { accessor: "tarih", Header: "Tarih", sortable: true },
    { accessor: "aciklama", Header: "Açıklama" },
    { 
      accessor: "giris", 
      Header: "Giriş", 
      Cell: ({ value }) => value > 0 ? (
        <Text color="green.600" fontWeight="semibold">+{value.toLocaleString()} ₺</Text>
      ) : "-"
    },
    { 
      accessor: "cikis", 
      Header: "Çıkış", 
      Cell: ({ value }) => value > 0 ? (
        <Text color="red.600" fontWeight="semibold">-{value.toLocaleString()} ₺</Text>
      ) : "-"
    },
    { 
      accessor: "bakiye", 
      Header: "Bakiye", 
      Cell: ({ value }) => (
        <Text fontWeight="bold">{value.toLocaleString()} ₺</Text>
      )
    }
  ];

  // Hesaplamalar
  const toplamBizeKesilen = bizeKesilen.reduce((total, fatura) => total + fatura.toplam, 0);
  const toplamKestigimiz = kestigimiz.reduce((total, fatura) => total + fatura.toplam, 0);
  const bekleyenOdemeler = kestigimiz.filter(f => f.durum === "Beklemede").reduce((total, f) => total + f.toplam, 0);

  const handleYeniFaturaOlustur = () => {
    toast({
      title: "Yeni Fatura",
      description: "Fatura oluşturma modalı açılacak",
      status: "info",
      duration: 2000
    });
  };

  const handleTopluIslem = () => {
    toast({
      title: "Toplu İşlem",
      description: "Toplu işlem modalı açılacak", 
      status: "info",
      duration: 2000
    });
  };

  useEffect(() => {
    // Sayfa yüklendiğinde loading'i kapat
    setTimeout(() => setLoading(false), 1000);
  }, []);

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }} gap={6}>
      {/* Ana Başlık */}
      <Flex justify="space-between" align="center">
        <VStack align="start" spacing={1}>
          <Text fontSize="2xl" fontWeight="bold" color="gray.700">
            Faturalama Yönetimi
          </Text>
          <Text fontSize="sm" color="gray.500">
            Gelir-gider takibi ve fatura yönetimi
          </Text>
        </VStack>
        <HStack spacing={3}>
          <Button
            leftIcon={<Icon as={FiPlus} />}
            colorScheme="blue"
            size="sm"
            onClick={handleYeniFaturaOlustur}
          >
            Yeni Fatura
          </Button>
          <Button
            leftIcon={<Icon as={FiFileText} />}
            variant="outline"
            size="sm"
            onClick={handleTopluIslem}
          >
            Toplu İşlem
          </Button>
        </HStack>
      </Flex>

      {/* Metrik Kartları */}
      <Grid templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(5, 1fr)" }} gap={4}>
        <GridItem>
          <Box p={5} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" shadow="sm">
            <Stat>
              <StatLabel fontSize="xs" color="gray.500">Aylık Gelir</StatLabel>
              <StatNumber fontSize="xl" color="green.600">
                <HStack spacing={2}>
                  <Icon as={FiDollarSign} />
                  <Text>{toplamKestigimiz.toLocaleString()} ₺</Text>
                </HStack>
              </StatNumber>
              <StatHelpText>
                <StatArrow type="increase" />
                23.36%
              </StatHelpText>
            </Stat>
          </Box>
        </GridItem>

        <GridItem>
          <Box p={5} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" shadow="sm">
            <Stat>
              <StatLabel fontSize="xs" color="gray.500">Aylık Gider</StatLabel>
              <StatNumber fontSize="xl" color="red.600">
                <HStack spacing={2}>
                  <Icon as={FiTrendingDown} />
                  <Text>{toplamBizeKesilen.toLocaleString()} ₺</Text>
                </HStack>
              </StatNumber>
              <StatHelpText>
                <StatArrow type="decrease" />
                9.05%
              </StatHelpText>
            </Stat>
          </Box>
        </GridItem>

        <GridItem>
          <Box p={5} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" shadow="sm">
            <Stat>
              <StatLabel fontSize="xs" color="gray.500">Net Kar</StatLabel>
              <StatNumber fontSize="xl" color="blue.600">
                {(toplamKestigimiz - toplamBizeKesilen).toLocaleString()} ₺
              </StatNumber>
              <StatHelpText>Bu ay</StatHelpText>
            </Stat>
          </Box>
        </GridItem>

        <GridItem>
          <Box p={5} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" shadow="sm">
            <Stat>
              <StatLabel fontSize="xs" color="gray.500">Bekleyen Ödemeler</StatLabel>
              <StatNumber fontSize="xl" color="orange.600">
                {bekleyenOdemeler.toLocaleString()} ₺
              </StatNumber>
              <StatHelpText>Tahsil edilecek</StatHelpText>
            </Stat>
          </Box>
        </GridItem>

        <GridItem>
          <Box p={5} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" shadow="sm">
            <Stat>
              <StatLabel fontSize="xs" color="gray.500">KDV Borcu</StatLabel>
              <StatNumber fontSize="xl" color="purple.600">
                12.300 ₺
              </StatNumber>
              <StatHelpText>Ödenecek</StatHelpText>
            </Stat>
          </Box>
        </GridItem>
      </Grid>

      {/* Fatura Tabloları */}
      <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" shadow="sm" overflow="hidden">
        <Tabs colorScheme="blue" variant="enclosed">
          <TabList bg="gray.50" px={6}>
            <Tab fontSize="sm" fontWeight="semibold">Bize Kesilen Faturalar ({bizeKesilen.length})</Tab>
            <Tab fontSize="sm" fontWeight="semibold">Kestiğimiz Faturalar ({kestigimiz.length})</Tab>
          </TabList>
          
          <TabPanels>
            <TabPanel p={0}>
              <DataTable
                columns={bizeKesilenColumns}
                data={bizeKesilen}
                loading={loading}
                tableFontSize="12px"
                tableRowHeight="40px"
              />
            </TabPanel>
            
            <TabPanel p={0}>
              <DataTable
                columns={kestigimizColumns}
                data={kestigimiz}
                loading={loading}
                tableFontSize="12px"
                tableRowHeight="40px"
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      {/* Hesap Hareketleri */}
      <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" shadow="sm" overflow="hidden">
        <Box px={6} py={4} bg="gray.50" borderBottom="1px solid" borderColor="gray.200">
          <HStack justify="space-between">
            <HStack spacing={2}>
              <Icon as={FiCreditCard} color="blue.500" />
              <Text fontSize="lg" fontWeight="bold" color="gray.700">
                Banka Hesap Hareketleri
              </Text>
            </HStack>
            <Badge colorScheme="green" px={3} py={1}>
              Güncel Bakiye: 14.550 ₺
            </Badge>
          </HStack>
        </Box>
        
        <DataTable
          columns={hesapHareketleriColumns}
          data={hesapHareketleri}
          loading={loading}
          tableFontSize="12px"
          tableRowHeight="40px"
        />
      </Box>
    </Flex>
  );
}